import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import {
  SubmissionStatus,
  WaitingOn,
  outcomeReasonRequired,
  type OutcomeReason as OutcomeReasonCode,
  type SetSubmissionStatus,
  type SubmissionStatus as SubmissionStatusCode,
} from '@clawhire/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { EMPTY, enumOptions, formatDate, formatPct, formatRate, formatRelativeDays } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { SimpleSelect } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';
import { SectionCard } from '../components/SectionCard';
import { StopRow } from '../components/StopRow';
import { useInvalidatePositions } from '../queries';
import type { PositionDetail, PositionSubmission, SubmissionStatusResponse } from '../types';
import { ProposeCandidateDialog } from './ProposeCandidateDialog';
import { SubmissionStatusDialog } from './SubmissionStatusDialog';

export interface SubmissionsSectionProps {
  position: PositionDetail;
  canEdit: boolean;
}

function candidateLabel(s: PositionSubmission): string {
  return s.candidate.label ?? s.candidate.fullName ?? s.candidate.candidateId;
}

/** Submissions of the position with inline status changes and "Propose candidate". */
export function SubmissionsSection({ position, canEdit }: SubmissionsSectionProps) {
  const navigate = useNavigate();
  const invalidate = useInvalidatePositions();
  const [proposeOpen, setProposeOpen] = React.useState(false);
  const [pending, setPending] = React.useState<{
    submission: PositionSubmission;
    status: SubmissionStatusCode;
  } | null>(null);

  const setStatus = useMutation({
    mutationFn: ({ id, body }: { id: string; body: SetSubmissionStatus }) =>
      api.post<SubmissionStatusResponse>(`/submissions/${encodeURIComponent(id)}/status`, body),
    onSuccess: async (res, vars) => {
      toast.success(`Status → ${SubmissionStatus.label(vars.body.status)}`);
      for (const h of res.hints ?? []) toast.info(h);
      for (const w of res.warnings ?? []) toast.warning(w.message);
      setPending(null);
      await invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onStatusChange = (s: PositionSubmission, status: SubmissionStatusCode) => {
    if (status === s.status) return;
    if (outcomeReasonRequired(status)) setPending({ submission: s, status });
    else setStatus.mutate({ id: s.id, body: { status } });
  };

  const submissions = position.submissions;
  const { submissionsCount, submittedCount } = position.computed;

  return (
    <SectionCard
      title="Submissions"
      description={
        submissionsCount === 0
          ? 'No candidates proposed yet.'
          : `${submissionsCount} candidate${submissionsCount === 1 ? '' : 's'}, ${submittedCount} submitted or later.`
      }
      actions={
        canEdit ? (
          <Button size="sm" onClick={() => setProposeOpen(true)}>
            <UserPlus aria-hidden="true" /> Propose candidate
          </Button>
        ) : null
      }
    >
      {submissions.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <caption className="sr-only">Submissions</caption>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Candidate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Candidate rate</TableHead>
                <TableHead className="text-right">Client offer</TableHead>
                <TableHead className="text-right">Actual margin</TableHead>
                <TableHead>Waiting on</TableHead>
                <TableHead>Follow-up</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.map((s) => {
                const label = candidateLabel(s);
                const actual = s.rates?.actualMarginPct ?? s.computed.actualMarginPct;
                const offer = s.rates?.offerInUse ?? s.clientRateEur;
                const belowTarget =
                  actual !== null &&
                  actual !== undefined &&
                  actual < s.rates.effectiveMargin.marginPct - 1e-9;
                return (
                  <TableRow
                    key={s.id}
                    tabIndex={0}
                    className="cursor-pointer focus-visible:bg-muted/60 focus-visible:outline-none"
                    onClick={() => navigate(`/submissions/${s.id}`)}
                    onKeyDown={(e) => {
                      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        navigate(`/submissions/${s.id}`);
                      }
                    }}
                  >
                    <TableCell>
                      <StopRow className="inline-flex flex-col">
                        <Link to={`/candidates/${s.candidate.id}`} className="font-medium hover:underline">
                          {label}
                        </Link>
                        <span className="font-mono text-xs text-muted-foreground">
                          {s.candidate.candidateId}
                        </span>
                      </StopRow>
                    </TableCell>
                    <TableCell>
                      {canEdit && !s.computed.isTerminal ? (
                        <StopRow>
                          <SimpleSelect
                            aria-label={`Status of ${label}`}
                            value={s.status}
                            options={enumOptions(SubmissionStatus)}
                            className="h-8 w-44"
                            disabled={setStatus.isPending && setStatus.variables?.id === s.id}
                            onChange={(v) => v && onStatusChange(s, v as SubmissionStatusCode)}
                          />
                        </StopRow>
                      ) : (
                        <span className="inline-flex flex-col gap-0.5">
                          <EnumBadge def={SubmissionStatus} value={s.status} />
                          {s.outcomeReason ? (
                            <span className="text-xs text-muted-foreground">
                              {s.outcomeReason.replace(/_/g, ' ').toLowerCase()}
                            </span>
                          ) : null}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatRate(s.candidateRateEur, s.candidateRateUnit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatRate(offer, 'HOUR')}</TableCell>
                    <TableCell className={cn('text-right tabular-nums', belowTarget && 'text-destructive')}>
                      {formatPct(actual)}
                    </TableCell>
                    <TableCell>
                      {s.waitingOn ? (
                        <EnumBadge def={WaitingOn} value={s.waitingOn} size="sm" />
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.followUpOn ? (
                        <span
                          className={cn(
                            'tabular-nums',
                            s.computed.needsChase && 'font-medium text-destructive',
                          )}
                        >
                          <time dateTime={s.followUpOn}>{formatDate(s.followUpOn)}</time>
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({formatRelativeDays(s.followUpOn)})
                          </span>
                        </span>
                      ) : s.computed.needsChase ? (
                        <span className="text-xs font-medium text-destructive">chase</span>
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : null}

      <SubmissionStatusDialog
        open={pending !== null}
        onOpenChange={(o) => {
          if (!o) setPending(null);
        }}
        candidateLabel={pending ? candidateLabel(pending.submission) : ''}
        status={pending?.status ?? null}
        busy={setStatus.isPending}
        onConfirm={(outcomeReason: OutcomeReasonCode, note) => {
          if (!pending) return;
          setStatus.mutate({
            id: pending.submission.id,
            body: { status: pending.status, outcomeReason, note },
          });
        }}
      />
      <ProposeCandidateDialog open={proposeOpen} onOpenChange={setProposeOpen} position={position} />
    </SectionCard>
  );
}
